import React, { useState, useEffect } from 'react';
import { getCdnImage } from '../lib/imageUtils';
import Lightbox from './Lightbox';

export default function GallerySection({ gallerySlug, title, subtitle, maxImages }) {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;
    async function loadGallery() {
      try {
        setLoading(true);
        const res = await fetch('https://api.dev.auxo.dk/public/content/galleri');
        if (!res.ok) throw new Error('Kunne ikke hente galleri');
        const data = await res.json();
        const items = Array.isArray(data) ? data : data.items || [];
        const match = items.find((g) => {
          const itemSlug = typeof g.slug === 'object' ? g.slug?._ || g.slug?.da : g.slug;
          return itemSlug === gallerySlug;
        });

        if (isMounted) {
          setGallery(match || null);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    if (gallerySlug) {
      loadGallery();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [gallerySlug]);

  const rawImages = gallery?.images || [];
  const displayImages = maxImages ? rawImages.slice(0, maxImages) : rawImages;

  if (loading) {
    return (
      <div className="py-12 flex justify-center items-center">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <i className="fa-solid fa-spinner fa-spin text-emerald-600 text-lg" aria-hidden="true" />
          <span>Indlæser galleri...</span>
        </div>
      </div>
    );
  }

  if (!displayImages || displayImages.length === 0) {
    return null;
  }

  const sectionTitle = title || gallery?.title || 'Billedgalleri';
  const sectionSubtitle = subtitle || gallery?.description || 'Dokumentation for vores udførte arbejde';

  return (
    <section className="py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
            <i className="fa-solid fa-camera mr-1.5" aria-hidden="true" />
            Fotodokumentation
          </span>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
            {sectionTitle}
          </h3>
          {sectionSubtitle && (
            <p className="text-slate-600 text-sm sm:text-base mt-1 max-w-2xl">
              {sectionSubtitle}
            </p>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {displayImages.map((imgUrl, idx) => (
          <div
            key={idx}
            onClick={() => {
              setActiveImageIndex(idx);
              setLightboxOpen(true);
            }}
            className="group relative overflow-hidden rounded-xl bg-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer aspect-4/3 border border-slate-200/80"
          >
            <img
              src={getCdnImage(imgUrl, { width: 800, quality: 85 })}
              alt={`${sectionTitle} - billede ${idx + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
            {/* Hover overlay with zoom icon */}
            <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                <i className="fa-solid fa-expand text-emerald-700 text-lg" aria-hidden="true" />
              </div>
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-center text-xs font-medium text-white bg-slate-950/60 backdrop-blur-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Klik for at forstørre
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      <Lightbox
        images={displayImages}
        currentIndex={activeImageIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setActiveImageIndex((prev) => (prev > 0 ? prev - 1 : displayImages.length - 1))}
        onNext={() => setActiveImageIndex((prev) => (prev < displayImages.length - 1 ? prev + 1 : 0))}
      />
    </section>
  );
}
